/* import __COLOCATED_TEMPLATE__ from './custom-action-started.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import Component from '@glimmer/component';
import type RenderablePart from 'embercom/objects/inbox/renderable-part';
import type CustomActionStarted from 'embercom/objects/inbox/renderable/custom-action-started';
import LinkWithText from 'embercom/helpers/link-with-text';
import type Session from 'embercom/services/session';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type RouterService from '@ember/routing/router-service';
// @ts-ignore
import { sanitizeHtml } from '@intercom/pulse/lib/sanitize';
import {
  truncateEventText,
  isEventTextTruncated,
  FEATURE_FLAG_NAME,
  KILLSWITCH_FLAG_NAME,
} from 'embercom/lib/inbox2/event-text-truncation';

interface CustomActionStartedPart extends RenderablePart {
  renderableData: CustomActionStarted;
}

interface Args {
  part: CustomActionStartedPart;
}

interface Signature {
  Args: Args;
}

export default class CustomActionStartedPartComponent extends Component<Signature> {
  @service declare session: Session;
  @service declare intl: IntlService;
  @service declare router: RouterService;

  // @ts-ignore helper doesn't seem to be typed correctly
  linkWithTextHelper = LinkWithText.create(this);

  get customActionName() {
    return this.args.part.renderableData.customActionName;
  }

  get sampleResponseDataLink() {
    if (this.customActionId) {
      let customActionBaseRoute = 'apps.app.settings.app-settings.custom-actions.custom-action';
      let href = this.router.urlFor(
        customActionBaseRoute,
        this.session.workspace.id,
        this.customActionId,
        {
          queryParams: {
            selectedTab: 'data_access_section',
          },
        },
      );
      let text = this.intl.t('inbox.custom-action-started-preview.sample-response-data');
      return sanitizeHtml(
        `<a target="_blank" rel="noopener noreferrer" class="hover:text-link  no-underline" href=${href}>${text}</a>`,
      );
    }
  }

  get customActionId() {
    return this.args.part.renderableData.customActionId;
  }

  get usedSampleResponseData() {
    return this.args.part.renderableData.usedSampleResponseData;
  }

  get renderableData() {
    return this.args.part.renderableData;
  }

  get admin() {
    return this.args.part.renderableData.adminSummary.name;
  }

  get isConversationEventsUiImprovementsEnabled() {
    return (
      (this.session.workspace?.isFeatureEnabled(FEATURE_FLAG_NAME) ?? false) &&
      !this.session.workspace?.isFeatureEnabled(KILLSWITCH_FLAG_NAME)
    );
  }

  truncateTitle(title: string | undefined): string {
    return truncateEventText(title);
  }

  isTitleTruncated(title: string | undefined): boolean {
    return isEventTextTruncated(title);
  }

  get combinedTooltipText(): string {
    let workflowTitle = this.args.part.renderableData.customBotSummary?.title;
    let actionName = this.customActionName;
    let bothTruncated = this.isTitleTruncated(workflowTitle) && this.isTitleTruncated(actionName);

    if (bothTruncated) {
      return this.intl.t('inbox.custom-action-started-custom-bot-tooltip', {
        workflowTitle,
        customActionName: actionName,
      });
    }
    return workflowTitle!;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationStream::EventParts::CustomActionStarted': typeof CustomActionStartedPartComponent;
    'inbox2/conversation-stream/event-parts/custom-action-started': typeof CustomActionStartedPartComponent;
  }
}
